import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { enregistrerEcran, fetchMiseEnService, type EcranMiseEnService, type EtatMiseEnService } from '@/api/onboarding'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Bot, Check, FileText, Mic, Sparkles, Voicemail } from 'lucide-react'
import { OnboardingNavyShell } from '@/components/onboarding/NavyShell'
import { OnboardingStepper } from '@/components/onboarding/Stepper'
import { GoogleAddressAutocomplete } from '@/components/GoogleAddressAutocomplete'
import { defaultLocale } from '@/lib/locales'
import { isAxiosError } from 'axios'
import { ApercuLangue, ApercuOptions, ApercuReponse, ApercuUrgence, DEGRADE_IA } from './Apercus'

/**
 * Mise en service après le paiement : une question par écran, le bon choix
 * déjà coché, « Continuer » suffit.
 *   2. Langue principale   3. Qui répond quand vous ne pouvez pas
 *   4. Options d'appel     5. Adresse 911
 * (1 = Bienvenue, 6 = Équipe, 7 = App mobile.)
 *
 * Chaque « Continuer » enregistre l'écran sur le serveur, qui l'applique au
 * numéro principal (services/mise_en_service.py). En revenant, le client
 * retrouve ses choix : ils sont relus du serveur.
 */

export const TOTAL_ETAPES = 7

type Reponse = 'ia' | 'messagerie'

interface Choix {
  /** Vide = pas encore choisie : on part de la langue de l'interface. */
  langue: '' | 'fr' | 'en'
  reponse: Reponse
  transcription: boolean
  resume: boolean
  enregistrement: boolean
  adresse: Adresse
  avisLu: boolean
  /** Hors du Canada et des États-Unis : pas d'adresse 911 à déclarer. */
  horsAmerique: boolean
}

interface Adresse {
  address1: string
  address2: string
  city: string
  province: string
  postal: string
}

// Le bon choix déjà coché : ce que le client obtient s'il ne touche à rien.
const CHOIX_DEFAUT: Choix = {
  langue: '',
  reponse: 'ia',
  transcription: true,
  resume: true,
  enregistrement: true,
  adresse: { address1: '', address2: '', city: '', province: '', postal: '' },
  avisLu: false,
  horsAmerique: false,
}

const ORDRE: EcranMiseEnService[] = ['langue', 'reponse', 'options', 'urgence']
const dejaFait = (etat: EtatMiseEnService, ecran: EcranMiseEnService) =>
  ORDRE.indexOf(etat.etape as EcranMiseEnService) >= ORDRE.indexOf(ecran)

/** Ce que le serveur a gardé ; un écran jamais enregistré garde son choix par défaut. */
function depuisServeur(etat: EtatMiseEnService | undefined): Choix {
  if (!etat) return CHOIX_DEFAUT
  const u = etat.urgence
  return {
    langue: etat.langue,
    reponse: etat.reponse || CHOIX_DEFAUT.reponse,
    ...(etat.options && dejaFait(etat, 'options')
      ? etat.options
      : { enregistrement: true, resume: true, transcription: true }),
    adresse: u
      ? { address1: u.address1, address2: u.address2, city: u.city, province: u.province, postal: u.postal }
      : CHOIX_DEFAUT.adresse,
    avisLu: !!u?.avis_lu,
    horsAmerique: u?.statut === 'sans_objet',
  }
}

/** Les choix de l'écran : ceux du serveur, puis ce que le client change avant « Continuer ». */
function useChoix() {
  const { data, isPending } = useQuery({ queryKey: ['mise-en-service'], queryFn: fetchMiseEnService })
  const [local, setLocal] = useState<Partial<Choix>>({})
  const choix = { ...depuisServeur(data), ...local }
  const changer = (partiel: Partial<Choix>) => setLocal((c) => ({ ...c, ...partiel }))
  return [choix, changer, isPending] as const
}

/** Enregistre l'écran puis passe au suivant ; une erreur reste sur l'écran. */
function useEnregistrer() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const mutation = useMutation({
    mutationFn: ({ ecran, donnees }: { ecran: EcranMiseEnService; donnees: Record<string, unknown>; suite: string }) =>
      enregistrerEcran(ecran, donnees),
    onSuccess: (etat, { suite }) => {
      queryClient.setQueryData(['mise-en-service'], etat)
      // Le garde du tableau de bord lit l'entreprise : elle vient de changer. Relue même si
      // aucun écran ne l'affiche ici : sinon le garde décidait sur la vieille copie et
      // renvoyait aux options après l'étape 7.
      void queryClient.invalidateQueries({ queryKey: ['companies'], refetchType: 'all' })
      navigate(suite)
    },
    onError: (err) => {
      // No number bought yet: the server refuses. The welcome screen buys it, or asks for the card.
      if (isAxiosError(err) && (err.response?.data as { error?: string } | undefined)?.error === 'numero_manquant') {
        navigate(`/${locale}/onboarding/welcome`, { replace: true, state: { fromCheckout: true } })
        return
      }
      toast.error(t('toasts.common.saveError'))
    },
  })
  return {
    enregistrer: (ecran: EcranMiseEnService, donnees: Record<string, unknown>, suite: string) =>
      mutation.mutate({ ecran, donnees, suite }),
    enCours: mutation.isPending,
  }
}

/** La langue principale choisie, sinon celle de l'interface. */
function languePrincipale(choix: Choix, locale: string): 'fr' | 'en' {
  return choix.langue || (locale === 'en' ? 'en' : 'fr')
}

/** L'avancement dans la barre du haut, sur la ligne du logo : le contenu démarre au titre. */
export function Avancement({ etape }: { etape: number }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3">
      <span
        className="hidden sm:inline text-[11px] font-semibold tracking-[0.1em] uppercase tabular-nums"
        style={{ color: '#8B9BB4' }}
      >
        {t('onboarding.miseEnService.stepLabel', { n: etape, total: TOTAL_ETAPES })}
      </span>
      <div
        className="flex gap-1 w-24 sm:w-36"
        aria-label={t('onboarding.miseEnService.stepLabel', { n: etape, total: TOTAL_ETAPES })}
      >
        {segments(etape).map((s, i) => (
          <span
            key={i}
            className="flex-1 h-[3px] rounded-full"
            style={{ backgroundColor: s.active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.12)' }}
          />
        ))}
      </div>
    </div>
  )
}

/** Segments de l'indicateur : tous ceux d'avant et l'étape courante sont pleins. */
export function segments(courante: number) {
  return Array.from({ length: TOTAL_ETAPES }, (_, i) => ({
    active: i < courante,
  }))
}

// ---------------------------------------------------------------------------
// Cadre commun
// ---------------------------------------------------------------------------

function Cadre({
  etape,
  titre,
  sousTitre,
  retour,
  onContinuer,
  continuerDesactive,
  apercu,
  children,
}: {
  etape: number
  titre: ReactNode
  sousTitre?: ReactNode
  retour?: string
  onContinuer: () => void
  continuerDesactive?: boolean
  /** Ce que le client obtient, à droite de la question (ordinateur seulement). */
  apercu?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // L'aperçu, quand il y en a un, occupe toute la moitié droite de l'écran.
  const pleinEcran = !!apercu
  return (
    <OnboardingNavyShell
      deconnexion={false}
      panneau={apercu}
      entete={pleinEcran ? <Avancement etape={etape} /> : undefined}
    >
      <main className="flex-grow flex items-center justify-center px-5 py-6 md:py-10">
        <div
          className={`w-full grid items-center gap-16 ${
            apercu && !pleinEcran ? 'max-w-[1120px] lg:grid-cols-[minmax(0,500px)_minmax(0,1fr)]' : 'max-w-[500px]'
          }`}
        >
          <div className="w-full max-w-[540px]">
            <div className={`h-7 mb-6 ${pleinEcran ? 'hidden' : ''}`}>
              {retour && (
                <button
                  type="button"
                  onClick={() => navigate(retour)}
                  className="-ml-1 px-1 text-[13px] font-medium inline-flex items-center gap-1.5 rounded-md hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  style={{ color: '#8B9BB4' }}
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {t('onboarding.miseEnService.back')}
                </button>
              )}
            </div>
            {!pleinEcran && (
              <OnboardingStepper
                label={t('onboarding.miseEnService.stepLabel', {
                  n: etape,
                  total: TOTAL_ETAPES,
                })}
                countLabel={`${etape} / ${TOTAL_ETAPES}`}
                steps={segments(etape)}
              />
            )}

            <div className="mb-8">
              <h1 className="text-[28px] md:text-[34px] font-semibold leading-[1.15] tracking-[-0.02em] text-balance">
                {titre}
              </h1>
              {sousTitre && (
                <p className="mt-3 text-[15px] leading-relaxed" style={{ color: '#8B9BB4' }}>
                  {sousTitre}
                </p>
              )}
            </div>

            {children}

            <div
              className={`mt-14 pt-6 border-t border-white/[0.06] flex flex-col-reverse gap-3 sm:flex-row sm:items-center ${pleinEcran && retour ? 'sm:justify-between' : 'sm:justify-end'}`}
            >
              {/* Retour en bas à gauche, sur la ligne de Continuer. */}
              {pleinEcran && retour && (
                <button
                  type="button"
                  onClick={() => navigate(retour)}
                  className="h-11 sm:-ml-2 px-2 text-[14px] font-medium inline-flex items-center justify-center gap-1.5 rounded-lg hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  style={{ color: '#8B9BB4' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('onboarding.miseEnService.back')}
                </button>
              )}
              <button
                type="button"
                onClick={onContinuer}
                disabled={continuerDesactive}
                className="w-full sm:w-auto h-11 px-7 rounded-xl text-[15px] font-semibold transition-all hover:bg-white/90 active:scale-[0.98] disabled:opacity-35 disabled:cursor-not-allowed disabled:active:scale-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                style={{ backgroundColor: 'white', color: '#0A192F' }}
              >
                {t('onboarding.miseEnService.continue')}
              </button>
            </div>
          </div>
          {!pleinEcran && apercu}
        </div>
      </main>
    </OnboardingNavyShell>
  )
}

/** Grande carte cliquable, un seul choix par groupe. */
function CarteChoix({
  choisie,
  onChoisir,
  icone,
  titre,
  badge,
  lang,
  ia = false,
  children,
}: {
  choisie: boolean
  onChoisir: () => void
  icone?: ReactNode
  titre: ReactNode
  badge?: string
  lang?: string
  /** Couleurs de l'IA du site (section Réceptionniste des tarifs). */
  ia?: boolean
  children?: ReactNode
}) {
  // Choisie + IA : contour en dégradé, sur un fond opaque équivalent au blanc 6,5 % sur le marine.
  const contourIa = choisie && ia
  return (
    <button
      type="button"
      role="radio"
      aria-checked={choisie}
      lang={lang}
      onClick={onChoisir}
      className={`group relative w-full h-full flex flex-col justify-start text-left rounded-2xl p-5 transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
        choisie ? '' : 'hover:bg-white/[0.045] hover:border-white/20'
      }`}
      style={
        contourIa
          ? {
              border: '2px solid transparent',
              background: `linear-gradient(#1A2940, #1A2940) padding-box, ${DEGRADE_IA} border-box`,
              boxShadow: '0 10px 30px -14px rgba(255,95,109,0.45)',
            }
          : {
              backgroundColor: choisie ? 'rgba(255,255,255,0.065)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${choisie ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.09)'}`,
              boxShadow: choisie ? '0 0 0 1px rgba(255,255,255,0.85), 0 8px 24px -12px rgba(0,0,0,0.6)' : 'none',
            }
      }
    >
      <span
        className="absolute top-5 right-5 w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all"
        style={{
          backgroundColor: choisie ? 'white' : 'transparent',
          border: choisie ? 'none' : '1.5px solid rgba(255,255,255,0.22)',
        }}
      >
        {choisie && <Check className="w-3 h-3" style={{ color: '#0A192F' }} strokeWidth={3.5} />}
      </span>
      <div className="flex flex-col items-start gap-4 pr-8">
        {icone && (
          <span
            className="w-9 h-9 rounded-[10px] flex items-center justify-center"
            style={
              ia
                ? { background: DEGRADE_IA, color: 'white' }
                : { backgroundColor: 'rgba(255,255,255,0.07)', color: '#E2E8F0' }
            }
          >
            {icone}
          </span>
        )}
        <div className="min-w-0 space-y-2">
          <div className="text-[16px] font-semibold leading-snug">{titre}</div>
          {badge && (
            <span
              className="inline-block text-[10.5px] font-semibold tracking-[0.06em] uppercase px-2 py-[3px] rounded-md"
              style={
                ia
                  ? {
                      background: 'linear-gradient(90deg, rgba(255,179,90,0.16), rgba(123,63,228,0.22))',
                      color: '#FFB38A',
                    }
                  : { backgroundColor: 'rgba(52,211,153,0.12)', color: '#4ADE80' }
              }
            >
              {badge}
            </span>
          )}
          {children && (
            <div className="text-sm leading-relaxed" style={{ color: '#A8B4C7' }}>
              {children}
            </div>
          )}
        </div>
      </div>
    </button>
  )
}

function Interrupteur({
  actif,
  onChange,
  icone,
  titre,
  desactive = false,
  note,
  description,
}: {
  actif: boolean
  onChange: (v: boolean) => void
  icone: ReactNode
  titre: string
  /** Grisé et éteint : l'option dépend d'une autre qui est coupée. */
  desactive?: boolean
  note?: string
  description?: string
}) {
  return (
    <div className={`py-3.5 flex items-center gap-3.5 transition-opacity ${desactive ? 'opacity-45' : ''}`}>
      <span
        className="flex-shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center"
        style={{ backgroundColor: 'rgba(255,255,255,0.07)', color: '#E2E8F0' }}
      >
        {icone}
      </span>
      <div className="flex-grow min-w-0">
        <div className="text-[15px] font-medium">{titre}</div>
        {description && !(desactive && note) && (
          <div className="text-[12.5px] mt-0.5" style={{ color: '#8B9BB4' }}>
            {description}
          </div>
        )}
        {desactive && note && (
          <div className="text-[12.5px] mt-0.5" style={{ color: '#8B9BB4' }}>
            {note}
          </div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={actif}
        aria-label={titre}
        disabled={desactive}
        onClick={() => onChange(!actif)}
        className="flex-shrink-0 relative w-12 h-7 rounded-full transition-colors disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        style={{
          backgroundColor: actif ? '#34D399' : 'rgba(255,255,255,0.15)',
        }}
      >
        <span
          className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform"
          style={{ transform: actif ? 'translateX(20px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// 2. Langue principale
// ---------------------------------------------------------------------------

export function OnboardingLanguePage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const [choix, changer, chargement] = useChoix()
  const { enregistrer, enCours } = useEnregistrer()
  // Langue de l'entreprise, distincte de celle de l'interface : un client
  // peut lire Callizy en français et répondre surtout en anglais. Choisir ici
  // ne change donc pas l'écran. Par défaut : la langue de l'interface.
  const langue = choix.langue || (locale === 'en' ? 'en' : 'fr')
  const choisir = (lng: 'fr' | 'en') => changer({ langue: lng })

  return (
    <Cadre
      etape={2}
      titre={
        <>
          <span lang="fr">Votre langue principale</span>
          <span className="block mt-0.5" lang="en" style={{ color: '#64748B' }}>
            Your main language
          </span>
        </>
      }
      retour={`/${locale}/onboarding/welcome`}
      onContinuer={() => enregistrer('langue', { langue }, `/${locale}/onboarding/reponse`)}
      continuerDesactive={chargement || enCours}
      apercu={<ApercuLangue locale={locale} langue={langue} />}
    >
      <div role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CarteChoix choisie={langue === 'fr'} onChoisir={() => choisir('fr')} titre="Français" lang="fr" />
        <CarteChoix choisie={langue === 'en'} onChoisir={() => choisir('en')} titre="English" lang="en" />
      </div>
      {/* Langue principale différente de l'interface : on propose, sans
          interrompre, d'afficher Callizy dans cette langue aussi. La phrase
          est écrite dans la langue proposée. */}
      {langue !== (locale === 'en' ? 'en' : 'fr') && (
        <button
          type="button"
          lang={langue}
          onClick={() => navigate(`/${langue}/onboarding/langue`, { replace: true })}
          className="mt-4 text-[13px] font-medium underline underline-offset-4 hover:text-white transition-colors"
          style={{ color: '#8B9BB4' }}
        >
          {langue === 'en' ? 'Show Callizy in English too?' : 'Afficher aussi Callizy en français ?'}
        </button>
      )}
    </Cadre>
  )
}

// ---------------------------------------------------------------------------
// 3. Qui répond quand vous ne pouvez pas ?
// ---------------------------------------------------------------------------

export function OnboardingReponsePage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const [choix, changer, chargement] = useChoix()
  const { enregistrer, enCours } = useEnregistrer()

  return (
    <Cadre
      etape={3}
      titre={t('onboarding.miseEnService.reponse.title')}
      sousTitre={t('onboarding.miseEnService.reponse.subtitle')}
      apercu={<ApercuReponse locale={locale} langue={languePrincipale(choix, locale)} ia={choix.reponse === 'ia'} />}
      retour={`/${locale}/onboarding/langue`}
      onContinuer={() => enregistrer('reponse', { reponse: choix.reponse }, `/${locale}/onboarding/options`)}
      continuerDesactive={chargement || enCours}
    >
      <div role="radiogroup" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CarteChoix
          choisie={choix.reponse === 'ia'}
          onChoisir={() => changer({ reponse: 'ia' })}
          icone={<Bot className="w-[18px] h-[18px]" />}
          ia
          titre={t('onboarding.miseEnService.reponse.ia.title')}
          badge={t('onboarding.miseEnService.reponse.recommended')}
        />
        <CarteChoix
          choisie={choix.reponse === 'messagerie'}
          onChoisir={() => changer({ reponse: 'messagerie' })}
          icone={<Voicemail className="w-[18px] h-[18px]" />}
          titre={t('onboarding.miseEnService.reponse.messagerie.title')}
        />
      </div>
    </Cadre>
  )
}

// ---------------------------------------------------------------------------
// 4. Options d'appel
// ---------------------------------------------------------------------------

export function OnboardingOptionsPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const [choix, changer, chargement] = useChoix()
  const { enregistrer, enCours } = useEnregistrer()

  return (
    <Cadre
      etape={4}
      titre={t('onboarding.miseEnService.options.title')}
      sousTitre={t('onboarding.miseEnService.options.subtitle')}
      apercu={
        <ApercuOptions
          locale={locale}
          langue={languePrincipale(choix, locale)}
          enregistrement={choix.enregistrement}
          resume={choix.resume}
          transcription={choix.transcription}
        />
      }
      retour={`/${locale}/onboarding/reponse`}
      onContinuer={() =>
        enregistrer(
          'options',
          { enregistrement: choix.enregistrement, resume: choix.resume, transcription: choix.transcription },
          `/${locale}/onboarding/urgence`,
        )
      }
      continuerDesactive={chargement || enCours}
    >
      <div
        className="rounded-2xl px-5 divide-y divide-white/[0.07]"
        style={{
          backgroundColor: 'rgba(255,255,255,0.025)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Interrupteur
          actif={choix.enregistrement}
          onChange={(v) => changer({ enregistrement: v })}
          icone={<Mic className="w-[18px] h-[18px]" />}
          titre={t('onboarding.miseEnService.options.enregistrement.title')}
        />
        {/* Turning the transcription off turns the summary off too: the summary is made from it. */}
        <Interrupteur
          actif={choix.transcription}
          onChange={(v) => changer(v ? { transcription: true } : { transcription: false, resume: false })}
          icone={<FileText className="w-[18px] h-[18px]" />}
          titre={t('onboarding.miseEnService.options.transcription.title')}
        />
        {/* The server transcribes then summarizes a call from its recording
            (generate_call_summary_task): without the recording or the
            transcription, this option would do nothing. Contact field
            suggestions (proposer_champs_fiche_task) come from the same task. */}
        <Interrupteur
          actif={choix.resume && choix.enregistrement && choix.transcription}
          onChange={(v) => changer({ resume: v })}
          icone={<Sparkles className="w-[18px] h-[18px]" />}
          titre={t('onboarding.miseEnService.options.resume.title')}
          description={t('onboarding.miseEnService.options.resume.desc')}
          desactive={!choix.enregistrement || !choix.transcription}
          note={t(
            choix.enregistrement
              ? 'onboarding.miseEnService.options.resume.needsTranscription'
              : 'onboarding.miseEnService.options.resume.needsRecording',
          )}
        />
      </div>
    </Cadre>
  )
}

// ---------------------------------------------------------------------------
// 5. Adresse 911
// ---------------------------------------------------------------------------

const PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Nova Scotia',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
]

// La valeur reste le nom anglais (celui que le serveur et le 911 attendent) ;
// seul l'affichage suit la langue.
const PROVINCES_FR: Record<string, string> = {
  'British Columbia': 'Colombie-Britannique',
  'New Brunswick': 'Nouveau-Brunswick',
  'Newfoundland and Labrador': 'Terre-Neuve-et-Labrador',
  'Nova Scotia': 'Nouvelle-Écosse',
  'Prince Edward Island': 'Île-du-Prince-Édouard',
  Quebec: 'Québec',
}

const nomProvince = (p: string, locale: string) => (locale === 'fr' ? PROVINCES_FR[p] || p : p)

/** « h1g1n9 » → « H1G 1N9 ». */
const formatPostal = (brut: string) => {
  const c = brut
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 6)
  return c.length > 3 ? `${c.slice(0, 3)} ${c.slice(3)}` : c
}

const champStyle = {
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: 'white',
} as const

export function OnboardingUrgencePage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const [choix, changer, chargement] = useChoix()
  const { enregistrer: envoyer, enCours } = useEnregistrer()

  const a = choix.adresse
  const complete = (x: Adresse) =>
    !!(x.address1.trim() && x.city.trim() && x.province && /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(x.postal.trim()))
  // Sans adresse enregistrée, les champs s'ouvrent d'office ; avec une, la carte et « Modifier ».
  const [ouverte, setEdition] = useState<boolean | null>(null)
  const edition = ouverte ?? !complete(a)
  const [brouillonLocal, setBrouillonLocal] = useState<Adresse | null>(null)
  const brouillon = brouillonLocal ?? a
  const setBrouillon = (f: (b: Adresse) => Adresse) => setBrouillonLocal((b) => f(b ?? a))

  const enregistrer = () => {
    changer({
      adresse: { ...brouillon, postal: formatPostal(brouillon.postal) },
    })
    setBrouillonLocal(null)
    setEdition(false)
  }

  // Remplie, l'adresse passe toujours : si Twilio la refuse, le serveur la garde « à corriger ».
  const continuer = () =>
    envoyer(
      'urgence',
      choix.horsAmerique ? { hors_amerique: true } : { ...a, avis_lu: choix.avisLu, hors_amerique: false },
      `/${locale}/onboarding/team`,
    )

  const champ = (cle: keyof Adresse, label: string, extra?: { placeholder?: string; maxLength?: number }) => (
    <label className="flex flex-col gap-1.5 text-left">
      <span className="text-xs font-semibold tracking-wide" style={{ color: '#94A3B8' }}>
        {label}
      </span>
      <input
        value={brouillon[cle]}
        onChange={(e) => setBrouillon((b) => ({ ...b, [cle]: e.target.value }))}
        className="h-11 px-3 rounded-lg outline-none focus:border-white"
        style={champStyle}
        {...extra}
      />
    </label>
  )

  return (
    <Cadre
      etape={5}
      titre={t('onboarding.miseEnService.urgence.title')}
      apercu={
        <ApercuUrgence
          locale={locale}
          ligne1={a.address1 + (a.address2 ? `, ${a.address2}` : '')}
          ligne2={`${a.city} (${nomProvince(a.province, locale)}) ${a.postal}`}
          horsAmerique={choix.horsAmerique}
        />
      }
      retour={`/${locale}/onboarding/options`}
      onContinuer={continuer}
      continuerDesactive={chargement || enCours || (!choix.horsAmerique && (edition || !choix.avisLu || !complete(a)))}
    >
      {choix.horsAmerique ? (
        <p className="text-[15px]" style={{ color: '#CBD5E1' }}>
          {t('onboarding.miseEnService.urgence.abroadNone')}
        </p>
      ) : (
        <>
          <div
            className="rounded-2xl p-5"
            style={{
              backgroundColor: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {edition ? (
              <div className="space-y-3">
                <div className="flex flex-col gap-1.5 text-left">
                  <span className="text-xs font-semibold tracking-wide" style={{ color: '#94A3B8' }}>
                    {t('onboarding.miseEnService.urgence.address1')}
                  </span>
                  <GoogleAddressAutocomplete
                    value={brouillon.address1}
                    onChange={(v) => setBrouillon((b) => ({ ...b, address1: v }))}
                    onPlaceSelected={({ address1, city, province, postalCode }) =>
                      setBrouillon((b) => ({
                        ...b,
                        address1,
                        city: city || b.city,
                        province: province || b.province,
                        postal: postalCode ? formatPostal(postalCode) : b.postal,
                      }))
                    }
                    className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>
                {champ('address2', t('onboarding.miseEnService.urgence.address2'), {
                  placeholder: t('onboarding.miseEnService.urgence.address2Placeholder'),
                })}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {champ('city', t('onboarding.miseEnService.urgence.city'))}
                  <label className="flex flex-col gap-1.5 text-left">
                    <span className="text-xs font-semibold tracking-wide" style={{ color: '#94A3B8' }}>
                      {t('onboarding.miseEnService.urgence.province')}
                    </span>
                    <select
                      value={brouillon.province}
                      onChange={(e) =>
                        setBrouillon((b) => ({
                          ...b,
                          province: e.target.value,
                        }))
                      }
                      className="h-11 px-2 rounded-lg outline-none"
                      style={champStyle}
                    >
                      <option value="" disabled>
                        {t('onboarding.miseEnService.urgence.provincePlaceholder')}
                      </option>
                      {PROVINCES.map((p) => (
                        <option key={p} value={p}>
                          {nomProvince(p, locale)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {champ('postal', t('onboarding.miseEnService.urgence.postal'), { maxLength: 7 })}
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={enregistrer}
                    disabled={!complete(brouillon)}
                    className="h-10 px-5 rounded-lg font-semibold text-sm disabled:opacity-40"
                    style={{ backgroundColor: 'white', color: '#0A192F' }}
                  >
                    {t('onboarding.miseEnService.urgence.save')}
                  </button>
                  {/* Annuler n'a de sens que s'il y a une adresse à laquelle revenir. */}
                  {complete(a) && (
                    <button
                      type="button"
                      onClick={() => {
                        setBrouillonLocal(null)
                        setEdition(false)
                      }}
                      className="h-10 px-4 rounded-lg text-sm font-medium"
                      style={{ color: '#94A3B8' }}
                    >
                      {t('onboarding.miseEnService.urgence.cancel')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold tracking-wide mb-1.5" style={{ color: '#94A3B8' }}>
                    {t('onboarding.miseEnService.urgence.declared')}
                  </div>
                  <div className="font-semibold leading-snug">
                    {a.address1}
                    {a.address2 ? `, ${a.address2}` : ''}
                  </div>
                  <div className="text-sm" style={{ color: '#A8B4C7' }}>
                    {a.city} ({nomProvince(a.province, locale)}) {a.postal}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setBrouillonLocal(null)
                    setEdition(true)
                  }}
                  className="flex-shrink-0 text-sm font-semibold underline underline-offset-4"
                >
                  {t('onboarding.miseEnService.urgence.edit')}
                </button>
              </div>
            )}
          </div>

          <label className="mt-6 flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={choix.avisLu}
              onChange={(e) => changer({ avisLu: e.target.checked })}
              className="w-5 h-5 accent-white flex-shrink-0 cursor-pointer"
            />
            <span className="text-sm font-medium">{t('onboarding.miseEnService.urgence.read')}</span>
          </label>
          {/* Les limites du 911 (obligation CRTC) : sur callizy.ca, pas à l'écran. */}
          <p className="mt-3 text-[13px]" style={{ color: '#8B9BB4' }}>
            {t('onboarding.miseEnService.urgence.learnMore')}{' '}
            <a
              href={`https://callizy.ca/${locale}/legal/911`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-white transition-colors"
              style={{ color: '#CBD5E1' }}
            >
              {t('onboarding.miseEnService.urgence.learnMoreLink')}
            </a>
          </p>
        </>
      )}

      <button
        type="button"
        onClick={() => {
          changer({ horsAmerique: !choix.horsAmerique })
          setEdition(false)
        }}
        className="mt-6 text-[13px] font-medium underline underline-offset-4 hover:text-white transition-colors"
        style={{ color: '#8B9BB4' }}
      >
        {choix.horsAmerique
          ? t('onboarding.miseEnService.urgence.inNorthAmerica')
          : t('onboarding.miseEnService.urgence.abroad')}
      </button>
    </Cadre>
  )
}
